import React, { useState } from 'react';
import { Form, Badge, Button } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { SignalCategory } from '../../types/context.types';
import {
  SIGNAL_CATEGORY_META,
  SIGNAL_CATEGORY_ORDER,
} from '../../constants/signalCategories';
import './ManualTagInput.css';

interface ManualTag {
  label: string;
  category: SignalCategory;
}

interface ManualTagInputProps {
  tags: ManualTag[];
  onTagAdd: (label: string, category: SignalCategory) => void;
  onTagRemove: (index: number) => void;
  maxTags?: number;
  compact?: boolean;
}

const ManualTagInput: React.FC<ManualTagInputProps> = ({
  tags,
  onTagAdd,
  onTagRemove,
  maxTags = 10,
  compact = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<SignalCategory>('goal');

  const meta = SIGNAL_CATEGORY_META[selectedCategory];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || tags.length >= maxTags) return;
    if (trimmed.length > 50) return;

    onTagAdd(trimmed, selectedCategory);
    setInputValue('');
  };

  return (
    <div className={`manual-tag-input${compact ? ' compact' : ''}`}>
      <div className="tag-input-row d-flex gap-2 mb-1">
        <Form.Select
          size="sm"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value as SignalCategory)}
          className="manual-tag-category-select"
          aria-label="Tag category"
        >
          {SIGNAL_CATEGORY_ORDER.map((value) => {
            const opt = SIGNAL_CATEGORY_META[value];
            return (
              <option key={value} value={value}>
                {opt.label} ({opt.subtitle})
              </option>
            );
          })}
        </Form.Select>
        <Form.Control
          size="sm"
          type="text"
          placeholder={meta.placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={50}
        />
        <Button
          size="sm"
          variant="outline-primary"
          className="manual-tag-add-btn"
          onClick={addTag}
          disabled={!inputValue.trim() || tags.length >= maxTags}
        >
          Add
        </Button>
      </div>

      <p className="manual-tag-input-tip text-muted small mb-2">{meta.tip}</p>

      {tags.length > 0 && (
        <div className="tag-badges d-flex flex-wrap gap-2">
          {tags.map((tag, i) => (
            <Badge
              key={i}
              className="tag-badge d-flex align-items-center gap-1"
              style={{
                backgroundColor: SIGNAL_CATEGORY_META[tag.category].color,
                fontSize: '0.8rem',
                fontWeight: 500,
                padding: '0.4em 0.6em',
              }}
            >
              {tag.label}
              <X
                size={14}
                style={{ cursor: 'pointer', marginLeft: '2px' }}
                onClick={() => onTagRemove(i)}
              />
            </Badge>
          ))}
        </div>
      )}

      {tags.length >= maxTags && (
        <p className="text-muted small mt-1">Maximum {maxTags} tags reached</p>
      )}
    </div>
  );
};

export default ManualTagInput;
